import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Loader2,
  FileBarChart,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Info,
  ArrowUpDown,
  AlertTriangle,
} from 'lucide-react';
import { REVENUE_FAMILY_CATEGORY_KEYS } from '../lib/transactionCategory';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  LabelList,
} from 'recharts';
import { formatCompact } from '../lib/chartFormatters';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { convertAmount, currencyLabel } from '../lib/currencyConvert';
import { reportsAPI, portfolioAPI, exchangeRatesAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { PortfolioMetricChart } from '../components/PortfolioMetricChart';
import { buildNetProfitChartData } from '../lib/pnlNetProfitChart';
import { PnlNetProfitChart } from '../components/dashboard/PnlNetProfitChart';
import { COST_BUCKET_LABELS_SHORT } from '../lib/costTaxonomy';
import { entityPnlYtdRows, sumRows, pnlSources } from '../lib/entityPnlRows';
import { RevenuePageContent } from './RevenuePage';
import { ExpensesPageContent } from './ExpensesPage';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  const sign = value < 0 ? '-' : '';
  return `${sign}${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const signedAmount = (value, currency = 'GBP') =>
  `${value >= 0 ? '+' : '-'}${formatAmount(Math.abs(value), currency)}`;

const categoryLabels = {
  revenue: 'Revenue',
  supplier_payment: 'Supplier Payments',
  intercompany: 'Intercompany',
  tax_payment: 'Tax Payments',
  subscription: 'Subscriptions',
  personal: 'Personal',
  loan: 'Loans',
  airbnb: 'Airbnb',
  booking_com: 'Booking.com',
  direct_booking: 'Direct Booking',
  bank_fee: 'Bank Fees',
  internal_transfer: 'Internal Transfers',
  other: 'Other',
};

const PIE_COLORS = [
  '#EF4444', '#F97316', '#8B5CF6', '#3B82F6',
  '#EC4899', '#EAB308', '#06B6D4', '#64748B',
  '#10B981', '#F43F5E', '#6366F1', '#14B8A6',
];

const entityLabels = {
  br_ltd: 'BR Ltd',
  eci: 'ECI',
  bi_ltd: 'BI Ltd',
  fan_zhang: 'Fan Zhang',
  fan_zhang_piotr_kacki: 'FZ & PK',
  piotr_kacki: 'Piotr Kacki',
};

function SummaryCard({ label, value, icon: Icon, color, sub, change }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold font-mono', sub)}>{value}</p>
            {change !== null && change !== undefined && (
              <p className={cn(
                'text-xs font-medium mt-0.5',
                change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {change >= 0 ? '+' : ''}{change.toFixed(0)}% YoY
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, currency = 'GBP' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono">{formatAmount(entry.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

// Source badge labels for entity P&L
const SOURCE_BADGE = {
  bros: { label: 'Source: BR OS', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
  portfolio: { label: 'Source: Property Portfolio', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
  business: { label: 'Source: BR OS + Property Portfolio', color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' },
  none: { label: 'No revenue source', color: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
};

function ProfitLossPageContent() {
  const { selectedEntity, selectedScope, entities } = useEntityStore();
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const isPortfolio = selectedScope === 'portfolio';
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [priorYear, setPriorYear] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [portfolioPnl, setPortfolioPnl] = useState(null);
  const [entityPnl, setEntityPnl] = useState(null);
  // Net Profit chart (dashboards round 3, commit 6), "All Entities" branch
  // only -- the canonical get_business_pnl monthly series, fetched fresh
  // here since the rest of this branch still reads the older consolidated
  // report pipeline for its stat cards/table (see fetchData below).
  const [aggregateEntityPnl, setAggregateEntityPnl] = useState(null);
  const [fxRates, setFxRates] = useState(null);
  const [pnlSortField, setPnlSortField] = useState('gross_revenue_native');
  const [pnlSortAsc, setPnlSortAsc] = useState(false);
  const [portfolioRegion, setPortfolioRegion] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pnlError, setPnlError] = useState(null);

  const resolveEntityId = () => {
    if (selectedEntity === 'all') return undefined;
    return selectedEntity;
  };

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    setPnlError(null);
    try {
      if (isPortfolio) {
        const data = await portfolioAPI.getPnl({ year });
        setPortfolioPnl(data);
        setReport(null);
        setPriorYear(null);
        setConsolidated(null);
        setEntityPnl(null);
      } else if (selectedEntity !== 'all') {
        // Single entity: use entity-pnl endpoint (source-routed)
        setPortfolioPnl(null);
        setConsolidated(null);
        setReport(null);
        setPriorYear(null);
        const entityId = resolveEntityId();
        const [pnlResult, ratesResult] = await Promise.all([
          reportsAPI.getEntityPnl({ year, entity_id: entityId }),
          exchangeRatesAPI.getLatest().catch(() => ({ data: null })),
        ]);
        setEntityPnl(pnlResult.data);
        setFxRates(ratesResult.data);
      } else {
        // All Entities: the same entity P&L the Dashboard reads
        // (get_business_pnl), not the old transaction-based consolidated
        // report, which counted personal entities and bank receipts as
        // revenue (Fan, 2026-10-25: one basis).
        setPortfolioPnl(null);
        setConsolidated(null);
        setReport(null);
        setPriorYear(null);
        setAggregateEntityPnl(null);
        const [pnlResult, ratesResult] = await Promise.all([
          reportsAPI.getEntityPnl({ year, scope: 'entities' }),
          exchangeRatesAPI.getLatest().catch(() => ({ data: null })),
        ]);
        setEntityPnl(pnlResult.data);
        setFxRates(ratesResult.data);
      }
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(msg);
      setPnlError(msg);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all' || isPortfolio) {
      fetchData();
    }
  }, [year, selectedEntity, entities]);

  useEffect(() => {
    if (!isPortfolio) setPortfolioRegion('all');
  }, [isPortfolio]);

  const summary = report?.summary;
  const priorSummary = priorYear?.summary;
  const reportCurrency = report?.currency || 'GBP';

  // Group P&L currency toggle: convert from report's native currency to
  // the display currency using the shared FX path (same as entity P&L).
  const groupRates = fxRates?.rates ?? fxRates ?? null;
  const groupCv = (v) => convertAmount(v, reportCurrency, displayCurrency, groupRates);
  const groupDisplayCurrency = displayCurrency;
  const groupCurrencyLabel = currencyLabel(reportCurrency, displayCurrency);
  const fmt = (v) => formatAmount(groupCv(v), groupDisplayCurrency);
  const signedFmt = (v) => signedAmount(groupCv(v), groupDisplayCurrency);

  const yoyChange = (current, prior) => {
    if (!prior || prior === 0) return null;
    return ((current - prior) / Math.abs(prior)) * 100;
  };

  // Chart data from monthly breakdown (converted to display currency)
  const chartData = useMemo(() => {
    if (!report) return [];
    const now = new Date();
    return report.months
      .filter((m) => {
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return m.month <= now.getMonth() + 1;
      })
      .map((m) => ({
        month: m.month_label.slice(0, 3),
        revenue: groupCv(m.revenue),
        expenses: groupCv(m.expenses),
        net_profit: groupCv(m.net_profit),
      }));
  }, [report, year, displayCurrency, groupRates]);

  // Net Profit (monthly) chart data (dashboards round 3, commit 6), memoized
  // like every other chart-data transform in this file rather than rebuilt
  // inline in JSX on every unrelated re-render (sort click, refresh spinner).
  const netProfitChartDataEntity = useMemo(() => {
    if (!entityPnl) return [];
    const epNativeCurrency = entityPnl.currency || 'GBP';
    const cv = (v) => convertAmount(parseFloat(v) || 0, epNativeCurrency, displayCurrency, groupRates);
    return buildNetProfitChartData({
      year,
      months: (entityPnl.months || []).map((m) => ({
        month: m.month,
        net_result: cv(m.net_result),
        net_result_actual: m.net_result_actual != null ? cv(m.net_result_actual) : null,
        net_result_forward: m.net_result_forward != null ? cv(m.net_result_forward) : null,
      })),
    });
  }, [entityPnl, year, displayCurrency, groupRates]);

  const netProfitChartDataAggregate = useMemo(() => {
    return buildNetProfitChartData({
      year,
      months: (aggregateEntityPnl?.months || []).map((m) => ({
        month: m.month,
        net_result: groupCv(m.net_result),
        net_result_actual: m.net_result_actual != null ? groupCv(m.net_result_actual) : null,
        net_result_forward: m.net_result_forward != null ? groupCv(m.net_result_forward) : null,
      })),
    });
    // groupCv closes over `report` (via reportCurrency) as well as
    // displayCurrency/groupRates -- `report` is a required dependency here
    // too, matching the established convention the sibling `chartData` memo
    // above already follows for the exact same groupCv closure, so this
    // can't return a stale conversion if report and aggregateEntityPnl ever
    // update on different ticks (e.g. one leg of the Promise.allSettled
    // fetch fails while the other succeeds).
  }, [aggregateEntityPnl, report, year, displayCurrency, groupRates]);

  // Aggregate expense categories across the year (exclude revenue/intercompany)
  const yearCategories = useMemo(() => {
    if (!report) return [];
    const totals = {};
    report.months.forEach((m) => {
      Object.entries(m.categories).forEach(([cat, amount]) => {
        if (REVENUE_FAMILY_CATEGORY_KEYS.includes(cat)) return;
        totals[cat] = (totals[cat] || 0) + amount;
      });
    });
    return Object.entries(totals)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, amount]) => ({
        name: categoryLabels[cat] || cat,
        value: Math.round(amount),
        key: cat,
      }));
  }, [report]);

  const totalCategorySpend = yearCategories.reduce((s, c) => s + c.value, 0);

  // Sorted by-property list for portfolio mode
  const sortedPortfolioProperties = useMemo(() => {
    if (!portfolioPnl?.by_property) return [];
    let filtered = portfolioPnl.by_property;
    if (portfolioRegion !== 'all') {
      filtered = filtered.filter(p => p.region === portfolioRegion);
    }
    // Native amounts mix GBP (UK) and EUR (FR): compare them in one currency,
    // or a GBP 150k flat ranks below a EUR 160k one that is worth less
    // (Fan, 2026-10-24: "rank the properties by revenue highest to lowest").
    const rate = parseFloat(portfolioPnl.fx_rates_used?.EUR_GBP) || 0.85;
    const comparable = (p) => {
      const raw = parseFloat(p[pnlSortField]) || 0;
      if (pnlSortField === 'margin_pct') return raw;
      return convertAmount(raw, p.currency, 'GBP', rate);
    };
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const aVal = comparable(a);
      const bVal = comparable(b);
      return pnlSortAsc ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [portfolioPnl, pnlSortField, pnlSortAsc, portfolioRegion]);

  // Normalized monthly series for the shared chart component (native currencies)
  const pnlMonthlySeries = useMemo(() => {
    if (!portfolioPnl?.monthly) return [];
    const now = new Date();
    return portfolioPnl.monthly
      .filter((m) => {
        const mn = parseInt(m.month.split('-')[1], 10);
        if (year < now.getFullYear()) return true;
        if (year > now.getFullYear()) return false;
        return mn <= now.getMonth() + 1;
      })
      .map((m) => ({
        month: MONTH_LABELS[parseInt(m.month.split('-')[1], 10) - 1].slice(0, 3),
        ukNative: m.uk?.net_result ?? 0,
        frNative: m.fr?.net_result ?? 0,
      }));
  }, [portfolioPnl, year]);

  const handlePnlSort = (field) => {
    if (pnlSortField === field) setPnlSortAsc(!pnlSortAsc);
    else { setPnlSortField(field); setPnlSortAsc(false); }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Portfolio P&L error state ──
  if (isPortfolio && pnlError && !portfolioPnl) {
    return (
      <div className="flex flex-col gap-8" data-testid="profit-loss-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profit & Loss</h1>
            <p className="text-muted-foreground mt-1">Revenue, expenses, and profitability overview</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => setYear(year - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium w-12 text-center">{year}</span>
            <Button variant="outline" size="icon" onClick={() => setYear(year + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        <Card>
          <CardContent className="py-16 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">P&L failed to load</h3>
            <p className="text-muted-foreground">{pnlError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Portfolio P&L ──
  if (isPortfolio && portfolioPnl) {
    const byRegion = portfolioPnl.by_region;
    const eurGbpRate = parseFloat(portfolioPnl.fx_rates_used?.EUR_GBP) || 0.85;
    const cv = (value, fromCurrency) => convertAmount(value, fromCurrency, displayCurrency, eurGbpRate);
    const now = new Date();
    const filteredMonths = portfolioPnl.monthly.filter((m) => {
      if (year < now.getFullYear()) return true;
      if (year > now.getFullYear()) return false;
      return parseInt(m.month.split('-')[1]) <= now.getMonth() + 1;
    });

    // Show unclassified column only when non-zero (avoids clutter in normal state).
    const hasUnclassified = filteredMonths.some(m =>
      (m.uk?.unclassified ?? 0) !== 0 || (m.fr?.unclassified ?? 0) !== 0
    );

    // Helper: sum a field across regions for a month, converting from native
    const monthCv = (m, field) =>
      cv(m.uk?.[field] ?? 0, 'GBP') + cv(m.fr?.[field] ?? 0, 'EUR');

    // Helper: region-aware value from by_region (guarded to prevent NaN)
    const regionVal = (field) =>
      portfolioRegion === 'uk' ? cv(byRegion.uk?.[field] ?? 0, 'GBP')
        : portfolioRegion === 'fr' ? cv(byRegion.fr?.[field] ?? 0, 'EUR')
        : cv(byRegion.uk?.[field] ?? 0, 'GBP') + cv(byRegion.fr?.[field] ?? 0, 'EUR');

    // Consolidated card values (actuals-to-date from by_region, scoped by backend)
    const cardGross = regionVal('gross_revenue');
    const cardCosts = regionVal('total_costs');
    const cardNet = regionVal('net_result');
    const cardCurrencyLabel = portfolioRegion === 'all' ? `${displayCurrency} approx`
      : currencyLabel(portfolioRegion === 'fr' ? 'EUR' : 'GBP', displayCurrency);

    // Total row: sum of displayed actual month rows (definitional reconciliation)
    const monthFieldSum = (field) =>
      filteredMonths.reduce((sum, m) => {
        if (portfolioRegion === 'uk') return sum + cv(m.uk?.[field] ?? 0, 'GBP');
        if (portfolioRegion === 'fr') return sum + cv(m.fr?.[field] ?? 0, 'EUR');
        return sum + cv(m.uk?.[field] ?? 0, 'GBP') + cv(m.fr?.[field] ?? 0, 'EUR');
      }, 0);
    const totalGross = monthFieldSum('gross_revenue');
    const totalOta = monthFieldSum('ota_commission');
    const totalMgmt = monthFieldSum('management_fees');
    const totalOps = monthFieldSum('operational');
    const totalCleaning = monthFieldSum('cleaning');
    const totalRepairs = monthFieldSum('repairs');
    const totalOtherOp = monthFieldSum('other_operational');
    const totalFin = monthFieldSum('financing');
    const totalUncl = monthFieldSum('unclassified');
    const totalNet = monthFieldSum('net_result');

    // Forward confirmed bookings (beyond actuals cutoff)
    const fwd = portfolioPnl.forward;
    const fwdRegionVal = (field) =>
      portfolioRegion === 'uk' ? cv(fwd?.uk?.[field] ?? 0, 'GBP')
        : portfolioRegion === 'fr' ? cv(fwd?.fr?.[field] ?? 0, 'EUR')
        : cv(fwd?.uk?.[field] ?? 0, 'GBP') + cv(fwd?.fr?.[field] ?? 0, 'EUR');
    const fwdGross = fwdRegionVal('gross_revenue');
    const fwdNet = fwdRegionVal('net_result');
    const fwdCount = portfolioRegion === 'uk' ? (fwd?.uk?.booking_count ?? 0)
      : portfolioRegion === 'fr' ? (fwd?.fr?.booking_count ?? 0)
      : (fwd?.uk?.booking_count ?? 0) + (fwd?.fr?.booking_count ?? 0);
    const hasForward = fwdGross > 0 || fwdNet !== 0;

    return (
      <div className="flex flex-col gap-8" data-testid="profit-loss-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Booking Profit & Loss</h1>
            <p className="text-muted-foreground mt-1">Property Portfolio</p>
          </div>
          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={isRefreshing}>
                <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
                <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-semibold w-12 text-center">{year}</span>
                <button
                  onClick={() => setYear((y) => y + 1)}
                  disabled={year >= now.getFullYear()}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Banner */}
          <div className="flex items-start gap-3 p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Booking volume is gross (before OTA commission). Costs = OTA commission + management fees + operational + financing. Net result = booking volume minus total costs. Includes recurring property costs where entered.
            </p>
          </div>

          {/* Region Tabs */}
          <Tabs value={portfolioRegion} onValueChange={setPortfolioRegion}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="fr">{'\uD83C\uDDEB\uD83C\uDDF7'} France</TabsTrigger>
              <TabsTrigger value="uk">{'\uD83C\uDDEC\uD83C\uDDE7'} UK</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Net Result Chart + 3 actuals-to-date cards + YTD sums (shared component) */}
          <PortfolioMetricChart
            cards={[
              {
                label: `Revenue YTD (${cardCurrencyLabel})`,
                value: formatAmount(cardGross, displayCurrency),
                icon: TrendingUp,
                color: 'bg-green-100 dark:bg-green-900/30',
                sub: 'text-green-600 dark:text-green-400',
              },
              {
                label: `Costs YTD (${cardCurrencyLabel})`,
                value: formatAmount(cardCosts, displayCurrency),
                icon: TrendingDown,
                color: 'bg-red-100 dark:bg-red-900/30',
                sub: 'text-red-600 dark:text-red-400',
              },
              {
                label: `Net Result YTD (${cardCurrencyLabel})`,
                value: formatAmount(cardNet, displayCurrency),
                icon: BarChart3,
                color: cardNet >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30',
                sub: cardNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
              },
            ]}
            monthlySeries={pnlMonthlySeries}
            forecastByMonth={null}
            title="Monthly Net Result"
            ukLabel="UK Net"
            frLabel="FR Net"
            displayCurrency={displayCurrency}
            eurGbpRate={eurGbpRate}
            year={year}
            portfolioRegion={portfolioRegion}
          />

          {/* Monthly P&L Table with cost breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly P&L ({cardCurrencyLabel})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Booking Volume</TableHead>
                    <TableHead className="text-right">{COST_BUCKET_LABELS_SHORT.ota_commission}</TableHead>
                    <TableHead className="text-right">{COST_BUCKET_LABELS_SHORT.management_fees}</TableHead>
                    <TableHead className="text-right">Cleaning</TableHead>
                    <TableHead className="text-right">Repairs</TableHead>
                    <TableHead className="text-right">Other</TableHead>
                    <TableHead className="text-right">{COST_BUCKET_LABELS_SHORT.financing}</TableHead>
                    {hasUnclassified && <TableHead className="text-right text-amber-600">{COST_BUCKET_LABELS_SHORT.unclassified}</TableHead>}
                    <TableHead className="text-right">Net Result</TableHead>
                    <TableHead className="text-right">Margin %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMonths.map((m) => {
                    // Built from the month number, not Date('YYYY-MM-01') (parsed as
                    // UTC midnight, it showed the PREVIOUS month west of UTC).
                    const monthLabel = new Date(2000, parseInt(m.month.slice(5, 7), 10) - 1, 1).toLocaleDateString('en-GB', { month: 'long' });
                    const isCurrentMonth = m.month === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` && year === now.getFullYear();

                    // Derive display values from native currency per-region data (same conversion path as cards)
                    const mGross = portfolioRegion === 'uk' ? cv(m.uk?.gross_revenue ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.gross_revenue ?? 0, 'EUR')
                      : monthCv(m, 'gross_revenue');
                    const mOta = portfolioRegion === 'uk' ? cv(m.uk?.ota_commission ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.ota_commission ?? 0, 'EUR')
                      : monthCv(m, 'ota_commission');
                    const mMgmt = portfolioRegion === 'uk' ? cv(m.uk?.management_fees ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.management_fees ?? 0, 'EUR')
                      : monthCv(m, 'management_fees');
                    const mOps = portfolioRegion === 'uk' ? cv(m.uk?.operational ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.operational ?? 0, 'EUR')
                      : monthCv(m, 'operational');
                    const mCleaning = portfolioRegion === 'uk' ? cv(m.uk?.cleaning ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.cleaning ?? 0, 'EUR')
                      : monthCv(m, 'cleaning');
                    const mRepairs = portfolioRegion === 'uk' ? cv(m.uk?.repairs ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.repairs ?? 0, 'EUR')
                      : monthCv(m, 'repairs');
                    const mOtherOp = portfolioRegion === 'uk' ? cv(m.uk?.other_operational ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.other_operational ?? 0, 'EUR')
                      : monthCv(m, 'other_operational');
                    const mFin = portfolioRegion === 'uk' ? cv(m.uk?.financing ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.financing ?? 0, 'EUR')
                      : monthCv(m, 'financing');
                    const mUncl = portfolioRegion === 'uk' ? cv(m.uk?.unclassified ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.unclassified ?? 0, 'EUR')
                      : monthCv(m, 'unclassified');
                    const mNet = portfolioRegion === 'uk' ? cv(m.uk?.net_result ?? 0, 'GBP')
                      : portfolioRegion === 'fr' ? cv(m.fr?.net_result ?? 0, 'EUR')
                      : monthCv(m, 'net_result');
                    const mMarginPct = mGross > 0 ? ((mNet / mGross) * 100).toFixed(1) : '0.0';
                    const hasData = mGross > 0 || mOta > 0 || mMgmt > 0 || mOps > 0;

                    return (
                      <TableRow
                        key={m.month}
                        className={cn(isCurrentMonth && 'bg-primary/5')}
                      >
                        <TableCell className="font-medium">
                          {monthLabel}
                          {isCurrentMonth && (
                            <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                          )}
                          {((portfolioRegion !== 'fr' && (m.uk?.estimated_costs ?? 0) > 0)
                            || (portfolioRegion !== 'uk' && (m.fr?.estimated_costs ?? 0) > 0)) && (
                            <Badge
                              className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-amber-500/10 text-amber-700 dark:text-amber-400 border-0"
                              title="Management fees, cleaning and repairs estimated: the management statement for this month is not in yet. Replaced by the real figures when it is imported."
                            >
                              Est.
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                          {hasData ? formatAmount(mGross, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {hasData ? formatAmount(mOta, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {hasData ? formatAmount(mMgmt, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {hasData ? formatAmount(mCleaning, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {hasData ? formatAmount(mRepairs, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {hasData ? formatAmount(mOtherOp, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {hasData ? formatAmount(mFin, displayCurrency) : '\u2014'}
                        </TableCell>
                        {hasUnclassified && (
                          <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                            {hasData ? formatAmount(mUncl, displayCurrency) : '\u2014'}
                          </TableCell>
                        )}
                        <TableCell className={cn(
                          'text-right font-mono font-semibold',
                          hasData && (mNet >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')
                        )}>
                          {hasData ? formatAmount(mNet, displayCurrency) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {hasData ? `${mMarginPct}%` : '\u2014'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals row: sum of displayed actual month rows */}
                  <TableRow className="border-t-2 font-semibold bg-muted/30">
                    <TableCell>Total (Actuals)</TableCell>
                    <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                      {formatAmount(totalGross, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {formatAmount(totalOta, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {formatAmount(totalMgmt, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {formatAmount(totalCleaning, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {formatAmount(totalRepairs, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {formatAmount(totalOtherOp, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                      {formatAmount(totalFin, displayCurrency)}
                    </TableCell>
                    {hasUnclassified && (
                      <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                        {formatAmount(totalUncl, displayCurrency)}
                      </TableCell>
                    )}
                    <TableCell className={cn(
                      'text-right font-mono',
                      totalNet >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    )}>
                      {formatAmount(totalNet, displayCurrency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {totalGross > 0 ? ((totalNet / totalGross) * 100).toFixed(1) : '0.0'}%
                    </TableCell>
                  </TableRow>
                  {/* Forward confirmed bookings (separate from actuals) */}
                  {hasForward && (
                    <TableRow className="text-muted-foreground bg-muted/10 italic">
                      <TableCell>Forward Confirmed ({fwdCount} booking{fwdCount !== 1 ? 's' : ''})</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatAmount(fwdGross, displayCurrency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                      {hasUnclassified && <TableCell className="text-right font-mono">{'\u2014'}</TableCell>}
                      <TableCell className="text-right font-mono">
                        {formatAmount(fwdNet, displayCurrency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{'\u2014'}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* By Property Table */}
          {sortedPortfolioProperties.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">By Property ({displayCurrency})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      {portfolioRegion === 'all' && <TableHead>Region</TableHead>}
                      <TableHead className="text-right cursor-pointer" onClick={() => handlePnlSort('gross_revenue_native')}>
                        <span className="flex items-center justify-end gap-1">Revenue <ArrowUpDown className="h-3 w-3" /></span>
                      </TableHead>
                      <TableHead className="text-right cursor-pointer" onClick={() => handlePnlSort('costs_native')}>
                        <span className="flex items-center justify-end gap-1">Costs <ArrowUpDown className="h-3 w-3" /></span>
                      </TableHead>
                      <TableHead className="text-right cursor-pointer" onClick={() => handlePnlSort('net_result_native')}>
                        <span className="flex items-center justify-end gap-1">Net Result <ArrowUpDown className="h-3 w-3" /></span>
                      </TableHead>
                      <TableHead className="text-right cursor-pointer" onClick={() => handlePnlSort('margin_pct')}>
                        <span className="flex items-center justify-end gap-1">Margin % <ArrowUpDown className="h-3 w-3" /></span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPortfolioProperties.map((p) => (
                      <TableRow key={p.property_id}>
                        <TableCell className="font-medium">
                          {p.name}
                          {p.managed_for_owner && (
                            <div
                              className="text-xs font-normal text-muted-foreground mt-0.5"
                              title="Managed for the owner: revenue here is our fee only. The guest bookings are the owner's money, shown for performance."
                            >
                              Guest bookings {formatAmount(cv(p.guest_bookings_native ?? 0, p.currency), displayCurrency)}
                              {' \u00b7 '}{p.guest_stays ?? 0} stays{' \u00b7 '}{p.guest_nights ?? 0} nights
                            </div>
                          )}
                        </TableCell>
                        {portfolioRegion === 'all' && (
                        <TableCell>
                          <Badge variant="outline" className={cn(
                            'text-[10px] px-1.5 py-0',
                            p.region === 'uk' ? 'border-blue-300 text-blue-600' : 'border-red-300 text-red-600'
                          )}>
                            {p.region === 'uk' ? 'UK' : 'FR'}
                          </Badge>
                          <span className="text-xs text-muted-foreground ml-1">{p.currency}</span>
                        </TableCell>
                        )}
                        <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                          {formatAmount(cv(p.gross_revenue_native ?? 0, p.currency), displayCurrency)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                          {formatAmount(cv(p.costs_native ?? 0, p.currency), displayCurrency)}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono font-semibold',
                          (p.net_result_native ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        )}>
                          {formatAmount(cv(p.net_result_native ?? 0, p.currency), displayCurrency)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">
                          {p.margin_pct != null ? `${p.margin_pct}%` : '\u2014'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
      </div>
    );
  }

  // ── Entity P&L (source-routed: bros / portfolio / none) ──
  if (entityPnl && !isPortfolio) {
    const src = entityPnl.source;
    const badge = SOURCE_BADGE[src] || SOURCE_BADGE.none;
    const epNativeCurrency = entityPnl.currency || 'GBP';
    // Currency toggle: convert from native to displayCurrency using shared conversion path.
    // Closures compare in native currency BEFORE display conversion; display is purely cosmetic.
    const epRates = fxRates?.rates ?? fxRates ?? null;
    const epCv = (v) => convertAmount(parseFloat(v) || 0, epNativeCurrency, displayCurrency, epRates);
    const epCurrency = displayCurrency;
    const epCurrencyLabel = currencyLabel(epNativeCurrency, displayCurrency);
    // epFmt: convert native value to display currency and format
    const epFmt = (v) => formatAmount(epCv(v), epCurrency);
    const now = new Date();

    // None source: empty state
    if (src === 'none') {
      return (
        <div className="flex flex-col gap-8" data-testid="profit-loss-page">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Profit & Loss</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={cn('text-xs', badge.color)}>{badge.label}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
                <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors"><ChevronLeft className="h-4 w-4" /></button>
                <span className="font-semibold w-12 text-center">{year}</span>
                <button onClick={() => setYear((y) => y + 1)} disabled={year >= now.getFullYear()} className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
          <Card>
            <CardContent className="py-16 text-center">
              <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No P&L data</h3>
              <p className="text-muted-foreground">{entityPnl.reason || 'This entity has no direct revenue source.'}</p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // One basis (Fan, 2026-10-25): rows, total and cards are the year-to-date
    // actuals the Dashboard shows (lib/entityPnlRows.js); later months are
    // forecast and appear only on the chart.
    const rows = entityPnlYtdRows(entityPnl.months, year);
    const tot = sumRows(rows);
    const srcs = pnlSources(entityPnl.months);
    const totalRevenue = tot.revenue;
    const totalCosts = tot.costs;
    const totalNet = tot.net;
    const isAllEntities = selectedEntity === 'all';
    const byEntity = isAllEntities ? (entityPnl.by_entity || []) : [];
    const byEntityNet = byEntity.reduce((acc, r) => acc + (parseFloat(r.net_result) || 0), 0);
    const byEntityRev = byEntity.reduce((acc, r) => acc + (parseFloat(r.revenue) || 0), 0);
    const internalRentAdj = totalNet - byEntityNet;
    const money = (v) => epFmt(v);
    const cell = 'text-right font-mono';
    const costCell = cn(cell, 'text-red-600 dark:text-red-400');
    const costCols = [
      ...(srcs.bros ? [
        { key: 'cogs', label: 'BR OS COGS' },
        { key: 'invoice_financing', label: 'Invoice Financing' },
        { key: 'entity_opex', label: 'BR OS opex' },
      ] : []),
      ...(srcs.portfolio ? [
        { key: 'ota_commission', label: COST_BUCKET_LABELS_SHORT?.ota_commission || 'OTA' },
        { key: 'management_fees', label: COST_BUCKET_LABELS_SHORT?.management_fees || 'Mgmt' },
        { key: 'cleaning', label: 'Cleaning' },
        { key: 'repairs', label: 'Repairs' },
        { key: 'other_operational', label: 'Other' },
        { key: 'financing', label: COST_BUCKET_LABELS_SHORT?.financing || 'Financing' },
      ] : []),
    ];
    const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    return (
      <div className="flex flex-col gap-8" data-testid="profit-loss-page">
        {/* Header with source badge */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profit & Loss</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={cn('text-xs', badge.color)}>{badge.label}</Badge>
              {entityPnl.property_count != null && <span className="text-xs text-muted-foreground">({entityPnl.property_count} properties)</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={isRefreshing}>
              <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />Refresh
            </Button>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
              <button onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors"><ChevronLeft className="h-4 w-4" /></button>
              <span className="font-semibold w-12 text-center">{year}</span>
              <button onClick={() => setYear((y) => y + 1)} disabled={year >= now.getFullYear()} className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {/* Summary cards: year to date, same figures as the Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <SummaryCard label="Revenue (year to date)" value={money(totalRevenue)} icon={TrendingUp} color="bg-green-100 dark:bg-green-900/30" sub="text-green-600 dark:text-green-400" />
          <SummaryCard label="Costs (year to date)" value={money(totalCosts)} icon={TrendingDown} color="bg-red-100 dark:bg-red-900/30" sub="text-red-600 dark:text-red-400" />
          <SummaryCard label="Net result (year to date)" value={signedAmount(epCv(totalNet), epCurrency)} icon={DollarSign}
            color={totalNet >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}
            sub={totalNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} />
        </div>

        <PnlNetProfitChart
          chartData={netProfitChartDataEntity}
          displayCurrency={epCurrency}
        />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly P&L ({epCurrencyLabel})</CardTitle>
            <p className="text-xs text-muted-foreground">
              Actuals to date. BR OS revenue is invoiced revenue; property revenue is bookings by check-in month. The current month counts as actual.
            </p>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  {costCols.map((c) => <TableHead key={c.key} className="text-right">{c.label}</TableHead>)}
                  <TableHead className="text-right">Total Costs</TableHead>
                  <TableHead className="text-right">Net Result</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const hasData = r.revenue !== 0 || r.costs !== 0;
                  const margin = r.revenue > 0 ? ((r.net / r.revenue) * 100).toFixed(1) : null;
                  const isCurrentMonth = r.month === nowKey;
                  return (
                    <TableRow key={r.month} className={cn(isCurrentMonth && 'bg-primary/5')}>
                      <TableCell className="font-medium">
                        {r.label}
                        {isCurrentMonth && <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>}
                      </TableCell>
                      <TableCell className={cn(cell, 'text-green-600 dark:text-green-400')}>{hasData ? money(r.revenue) : '\u2014'}</TableCell>
                      {costCols.map((c) => <TableCell key={c.key} className={costCell}>{hasData ? money(r[c.key]) : '\u2014'}</TableCell>)}
                      <TableCell className={costCell}>{hasData ? money(r.costs) : '\u2014'}</TableCell>
                      <TableCell className={cn(cell, 'font-semibold', hasData && (r.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'))}>
                        {hasData ? signedAmount(epCv(r.net), epCurrency) : '\u2014'}
                      </TableCell>
                      <TableCell className={cn(cell, 'text-muted-foreground')}>{hasData && margin !== null ? `${margin}%` : '\u2014'}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-t-2 font-semibold bg-muted/30">
                  <TableCell>Total</TableCell>
                  <TableCell className={cn(cell, 'text-green-600 dark:text-green-400')}>{money(totalRevenue)}</TableCell>
                  {costCols.map((c) => <TableCell key={c.key} className={costCell}>{money(tot[c.key])}</TableCell>)}
                  <TableCell className={costCell}>{money(totalCosts)}</TableCell>
                  <TableCell className={cn(cell, totalNet >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>{signedAmount(epCv(totalNet), epCurrency)}</TableCell>
                  <TableCell className={cn(cell, 'text-muted-foreground')}>{totalRevenue > 0 ? `${((totalNet / totalRevenue) * 100).toFixed(1)}%` : '\u2014'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {byEntity.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">By entity, year to date ({epCurrencyLabel})</CardTitle>
              <p className="text-xs text-muted-foreground">
                Each entity's own P&L. Bonjour Residences includes the flat it owns. Rent one of our entities pays another is a cost in that entity's own view and is taken out of the All Entities total.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entity</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Costs</TableHead>
                    <TableHead className="text-right">Net Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byEntity.map((r) => (
                    <TableRow key={r.entity_id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className={cn(cell, 'text-green-600 dark:text-green-400')}>{money(r.revenue)}</TableCell>
                      <TableCell className={costCell}>{money(r.costs)}</TableCell>
                      <TableCell className={cn(cell, (parseFloat(r.net_result) || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>{signedAmount(epCv(r.net_result), epCurrency)}</TableCell>
                    </TableRow>
                  ))}
                  {Math.abs(internalRentAdj) >= 0.5 && (
                    <TableRow>
                      <TableCell className="italic text-muted-foreground">Rent between our entities (internal)</TableCell>
                      <TableCell className={cell}>{money(totalRevenue - byEntityRev)}</TableCell>
                      <TableCell className={cell}>{money(-internalRentAdj + (totalRevenue - byEntityRev))}</TableCell>
                      <TableCell className={cell}>{signedAmount(epCv(internalRentAdj), epCurrency)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow className="border-t-2 font-semibold bg-muted/30">
                    <TableCell>All Entities</TableCell>
                    <TableCell className={cn(cell, 'text-green-600 dark:text-green-400')}>{money(totalRevenue)}</TableCell>
                    <TableCell className={costCell}>{money(totalCosts)}</TableCell>
                    <TableCell className={cell}>{signedAmount(epCv(totalNet), epCurrency)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8" data-testid="profit-loss-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profit & Loss</h1>
          <p className="text-muted-foreground mt-1">Revenue, expenses, and profitability overview</p>
          </div>

          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>

            {/* Year selector */}
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
              <button
                onClick={() => setYear((y) => y - 1)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-semibold w-12 text-center">{year}</span>
              <button
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= new Date().getFullYear()}
                className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              label="Revenue"
              value={fmt(summary.total_revenue)}
              icon={TrendingUp}
              color="bg-green-100 dark:bg-green-900/30"
              sub="text-green-600 dark:text-green-400"
              change={yoyChange(summary.total_revenue, priorSummary?.total_revenue)}
            />
            <SummaryCard
              label="Expenses"
              value={fmt(summary.total_expenses)}
              icon={TrendingDown}
              color="bg-red-100 dark:bg-red-900/30"
              sub="text-red-600 dark:text-red-400"
              change={yoyChange(summary.total_expenses, priorSummary?.total_expenses)}
            />
            <SummaryCard
              label="Gross Profit"
              value={signedFmt(summary.gross_profit)}
              icon={BarChart3}
              color={summary.gross_profit >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}
              sub={summary.gross_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
              change={yoyChange(summary.gross_profit, priorSummary?.gross_profit)}
            />
            <SummaryCard
              label="Net Profit"
              value={signedFmt(summary.net_profit)}
              icon={DollarSign}
              color={summary.net_profit >= 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30'}
              sub={summary.net_profit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}
              change={yoyChange(summary.net_profit, priorSummary?.net_profit)}
            />
          </div>
        )}

        {/* Net Profit (monthly) chart (dashboards round 3, commit 6):
            directly below the stat cards, from the canonical get_business_pnl
            predicate (aggregateEntityPnl, GBP-native) -- independent of the
            older consolidated-report pipeline the cards/table above use,
            converted to displayCurrency with the same groupCv this branch
            already applies elsewhere. */}
        <PnlNetProfitChart
          chartData={netProfitChartDataAggregate}
          displayCurrency={displayCurrency}
        />

        {/* Monthly Trend Chart */}
        {report && chartData.some((d) => d.revenue > 0 || d.expenses > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="month"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                    tickFormatter={(v) => `${CURRENCY_SYMBOLS[groupDisplayCurrency] || groupDisplayCurrency}${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<ChartTooltip currency={groupDisplayCurrency} />} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="revenue" position="top" formatter={(v) => formatCompact(v, groupDisplayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#10B981' }} />
                  </Bar>
                  <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="expenses" position="top" formatter={(v) => formatCompact(v, groupDisplayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#EF4444' }} />
                  </Bar>
                  <Line
                    type="monotone"
                    dataKey="net_profit"
                    name="Net Profit"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#3B82F6' }}
                  >
                    <LabelList dataKey="net_profit" position="bottom" formatter={(v) => formatCompact(v, groupDisplayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#3B82F6' }} offset={8} />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="monthly">
          <TabsList>
            <TabsTrigger value="monthly">Monthly Breakdown</TabsTrigger>
            <TabsTrigger value="categories">Expense Categories</TabsTrigger>
            {selectedEntity === 'all' && (
              <TabsTrigger value="entity">By Entity</TabsTrigger>
            )}
          </TabsList>

          {/* Monthly Breakdown Table */}
          <TabsContent value="monthly" className="space-y-4">
            {selectedEntity === 'all' && consolidated?.entity_months ? (() => {
              const em = consolidated.entity_months;
              const activeEntities = Object.entries(em)
                .filter(([, months]) => months.some((m) => m.revenue > 0 || m.expenses > 0))
                .map(([id]) => id);
              const now = new Date();
              const monthFilter = (m) => {
                if (year < now.getFullYear()) return true;
                if (year > now.getFullYear()) return false;
                return m.month <= now.getMonth() + 1;
              };
              return (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          {activeEntities.map((id) => (
                            <TableHead key={id} className="text-right">{entityLabels[id] || id}</TableHead>
                          ))}
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(em[activeEntities[0]] || []).filter(monthFilter).map((_, mi) => {
                          const mn = mi + 1;
                          const isCurrentMonth = mn === now.getMonth() + 1 && year === now.getFullYear();
                          const monthLabel = em[activeEntities[0]][mi].month_label;
                          const total = activeEntities.reduce((s, id) => s + (em[id]?.[mi]?.net_profit || 0), 0);
                          return (
                            <TableRow key={mn} className={cn(isCurrentMonth && 'bg-primary/5')}>
                              <TableCell className="font-medium">
                                {monthLabel}
                                {isCurrentMonth && (
                                  <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>
                                )}
                              </TableCell>
                              {activeEntities.map((id) => {
                                const np = em[id]?.[mi]?.net_profit || 0;
                                const hasData = (em[id]?.[mi]?.revenue || 0) > 0 || (em[id]?.[mi]?.expenses || 0) > 0;
                                return (
                                  <TableCell key={id} className={cn(
                                    'text-right font-mono',
                                    hasData && (np >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')
                                  )}>
                                    {hasData ? signedFmt(np) : '—'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className={cn(
                                'text-right font-mono font-semibold',
                                total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              )}>
                                {signedFmt(total)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow className="border-t-2 font-semibold bg-muted/30">
                          <TableCell>Total</TableCell>
                          {activeEntities.map((id) => {
                            const total = (em[id] || []).reduce((s, m) => s + m.net_profit, 0);
                            return (
                              <TableCell key={id} className={cn(
                                'text-right font-mono',
                                total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              )}>
                                {signedFmt(total)}
                              </TableCell>
                            );
                          })}
                          <TableCell className={cn(
                            'text-right font-mono font-semibold',
                            (() => {
                              const gt = activeEntities.reduce((s, id) => s + (em[id] || []).reduce((s2, m) => s2 + m.net_profit, 0), 0);
                              return gt >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                            })()
                          )}>
                            {signedFmt(activeEntities.reduce((s, id) => s + (em[id] || []).reduce((s2, m) => s2 + m.net_profit, 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })() : report ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Expenses</TableHead>
                        <TableHead className="text-right">Gross Profit</TableHead>
                        <TableHead className="text-right">Tax</TableHead>
                        <TableHead className="text-right">Net Profit</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.months.filter((m) => {
                        if (year < new Date().getFullYear()) return true;
                        if (year > new Date().getFullYear()) return false;
                        return m.month <= new Date().getMonth() + 1;
                      }).map((m) => {
                        const hasData = m.transaction_count > 0 || m.paid_invoice_count > 0;
                        const margin = m.revenue > 0 ? (m.net_profit / m.revenue) * 100 : null;
                        const isCurrentMonth = m.month === new Date().getMonth() + 1 && year === new Date().getFullYear();
                        return (
                          <TableRow key={m.month} className={cn(isCurrentMonth && 'bg-primary/5')}>
                            <TableCell className="font-medium">
                              {m.month_label}
                              {isCurrentMonth && (
                                <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">
                                  Now
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                              {hasData ? fmt(m.revenue) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                              {hasData ? fmt(m.expenses) : '—'}
                            </TableCell>
                            <TableCell className={cn(
                              'text-right font-mono',
                              hasData && (m.gross_profit >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400')
                            )}>
                              {hasData ? signedFmt(m.gross_profit) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-orange-600 dark:text-orange-400">
                              {hasData ? fmt(m.tax_paid) : '—'}
                            </TableCell>
                            <TableCell className={cn(
                              'text-right font-mono font-semibold',
                              hasData && (m.net_profit >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400')
                            )}>
                              {hasData ? signedFmt(m.net_profit) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {hasData && margin !== null ? `${margin.toFixed(1)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals row */}
                      {summary && (
                        <TableRow className="border-t-2 font-semibold bg-muted/30">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                            {fmt(summary.total_revenue)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                            {fmt(summary.total_expenses)}
                          </TableCell>
                          <TableCell className={cn(
                            'text-right font-mono',
                            summary.gross_profit >= 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          )}>
                            {signedFmt(summary.gross_profit)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-orange-600 dark:text-orange-400">
                            {fmt(summary.total_tax_paid)}
                          </TableCell>
                          <TableCell className={cn(
                            'text-right font-mono',
                            summary.net_profit >= 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          )}>
                            {signedFmt(summary.net_profit)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-muted-foreground">
                            {summary.total_revenue > 0
                              ? `${((summary.net_profit / summary.total_revenue) * 100).toFixed(1)}%`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : pnlError ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
                  <h3 className="text-lg font-medium mb-2">P&L failed to load</h3>
                  <p className="text-muted-foreground">{pnlError}</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Data for {year}</h3>
                  <p className="text-muted-foreground">
                    Sync your integrations to populate financial data.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Expense Categories */}
          <TabsContent value="categories" className="space-y-4">
            {yearCategories.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Category Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={yearCategories}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          dataKey="value"
                          paddingAngle={2}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {yearCategories.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => fmt(value)}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Category list */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {yearCategories.map((cat, i) => {
                        const pct = totalCategorySpend > 0
                          ? ((cat.value / totalCategorySpend) * 100).toFixed(1)
                          : '0.0';
                        return (
                          <div key={cat.key} className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between text-sm">
                                <span className="font-medium truncate">{cat.name}</span>
                                <span className="font-mono text-muted-foreground ml-2">{pct}%</span>
                              </div>
                              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                                  }}
                                />
                              </div>
                            </div>
                            <span className="font-mono text-sm font-medium w-20 text-right shrink-0">
                              {fmt(cat.value)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {totalCategorySpend > 0 && (
                      <div className="mt-4 pt-4 border-t flex justify-between text-sm font-semibold">
                        <span>Total</span>
                        <span className="font-mono">{fmt(totalCategorySpend)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileBarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No category data for {year}</h3>
                  <p className="text-muted-foreground">
                    Transaction categories will appear once transactions are synced.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* By Entity (consolidated) */}
          {selectedEntity === 'all' && (
            <TabsContent value="entity" className="space-y-4">
              {consolidated ? (
                <>
                  {/* Entity comparison chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Entity Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(consolidated.entity_pnl).map(([id, pnl]) => ({
                            entity: entityLabels[id] || id,
                            revenue: groupCv(pnl.revenue),
                            expenses: groupCv(pnl.expenses),
                            net_profit: groupCv(pnl.net_profit),
                          }))}
                          margin={{ top: 30, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="entity" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                          <YAxis
                            stroke="hsl(var(--muted-foreground))"
                            fontSize={12}
                            tickFormatter={(v) => `${CURRENCY_SYMBOLS[groupDisplayCurrency] || groupDisplayCurrency}${(v / 1000).toFixed(0)}k`}
                          />
                          <Tooltip content={<ChartTooltip currency={groupDisplayCurrency} />} />
                          <Legend />
                          <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="revenue" position="top" formatter={(v) => formatCompact(v, groupDisplayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#10B981' }} />
                          </Bar>
                          <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="expenses" position="top" formatter={(v) => formatCompact(v, groupDisplayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#EF4444' }} />
                          </Bar>
                          <Bar dataKey="net_profit" name="Net Profit" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="net_profit" position="top" formatter={(v) => formatCompact(v, groupDisplayCurrency)} style={{ fontSize: 10, fontWeight: 600, fill: '#3B82F6' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Entity P&L table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Consolidated P&L</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Entity</TableHead>
                            <TableHead className="text-right">Revenue</TableHead>
                            <TableHead className="text-right">Expenses</TableHead>
                            <TableHead className="text-right">Gross Profit</TableHead>
                            <TableHead className="text-right">Tax</TableHead>
                            <TableHead className="text-right">Net Profit</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(consolidated.entity_pnl).map(([id, pnl]) => (
                            <TableRow key={id}>
                              <TableCell className="font-medium">{entityLabels[id] || id}</TableCell>
                              <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                                {fmt(pnl.revenue)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                                {fmt(pnl.expenses)}
                              </TableCell>
                              <TableCell className={cn(
                                'text-right font-mono',
                                pnl.gross_profit >= 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              )}>
                                {signedFmt(pnl.gross_profit)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-orange-600 dark:text-orange-400">
                                {fmt(pnl.tax_paid)}
                              </TableCell>
                              <TableCell className={cn(
                                'text-right font-mono font-semibold',
                                pnl.net_profit >= 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              )}>
                                {signedFmt(pnl.net_profit)}
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* Intercompany eliminations row */}
                          {consolidated.intercompany_eliminations.entries_count > 0 && (
                            <TableRow className="text-muted-foreground italic">
                              <TableCell>Intercompany Eliminations</TableCell>
                              <TableCell className="text-right font-mono">
                                -{fmt(consolidated.intercompany_eliminations.revenue_eliminated)}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                -{fmt(consolidated.intercompany_eliminations.expense_eliminated)}
                              </TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell />
                            </TableRow>
                          )}

                          {/* Consolidated totals */}
                          <TableRow className="border-t-2 font-semibold bg-muted/30">
                            <TableCell>Consolidated</TableCell>
                            <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                              {fmt(consolidated.consolidated.revenue_after_elimination)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                              {fmt(consolidated.consolidated.expenses_after_elimination)}
                            </TableCell>
                            <TableCell className={cn(
                              'text-right font-mono',
                              consolidated.consolidated.gross_profit >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            )}>
                              {signedFmt(consolidated.consolidated.gross_profit)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-orange-600 dark:text-orange-400">
                              {fmt(consolidated.consolidated.total_tax_paid)}
                            </TableCell>
                            <TableCell className={cn(
                              'text-right font-mono',
                              consolidated.consolidated.net_profit >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            )}>
                              {signedFmt(consolidated.consolidated.net_profit)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading consolidated data...</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
    </div>
  );
}

const SECTION_TABS = [
  { key: 'pnl', label: 'Profit & Loss' },
  { key: 'revenue', label: 'Revenues' },
  { key: 'costs', label: 'Costs' },
];

export function ProfitLossPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') ?? 'pnl';

  const handleSectionChange = (key) => {
    if (key === 'pnl') {
      setSearchParams({});
    } else {
      setSearchParams({ section: key });
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        {/* Section tabs */}
        <div
          className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit"
          data-testid="pnl-section-tabs"
        >
          {SECTION_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleSectionChange(tab.key)}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                section === tab.key && 'bg-background text-foreground shadow'
              )}
              data-testid={'pnl-section-tab-' + tab.key}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Section content */}
        {section === 'pnl' && <ProfitLossPageContent />}
        {section === 'revenue' && <RevenuePageContent />}
        {section === 'costs' && <ExpensesPageContent />}
      </div>
    </DashboardLayout>
  );
}
